import React, { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEnseignant } from '../../hooks/useEnseignant';
import { useStatut } from '../../hooks/useStatut';
import EnseignantTable from './EnseignantTable';
import EnseignantForm from './EnseignantForm';
import Modal from '../Modal';
import ConfirmationModal from '../ConfirmationModal';
import CreateEnseignantAccountModal from '../CreateEnseignantAccountModal';

const EnseignantTab = ({ triggerAdd = 0, triggerRefresh = 0 }) => {
  const {
    enseignants,
    loading,
    submitting,
    deleteLoading,
    searchTerm,
    pagination,
    handleSearch,
    handlePageChange,
    createEnseignant,
    updateEnseignant,
    deleteEnseignant,
    fetchEnseignants
  } = useEnseignant();

  // Récupérer les statuts pour le formulaire
  const { statuts: statusEnseignants } = useStatut();

  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEnseignant, setSelectedEnseignant] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [enseignantToDelete, setEnseignantToDelete] = useState(null);
  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] = useState(false);
  const [enseignantForAccount, setEnseignantForAccount] = useState(null);

  // Réagir au déclencheur d'ajout depuis le bouton principal
  useEffect(() => {
    if (triggerAdd > 0) {
      handleAdd();
    }
  }, [triggerAdd]);

  // Réagir au déclencheur de rafraîchissement
  useEffect(() => {
    if (triggerRefresh > 0) {
      fetchEnseignants();
    }
  }, [triggerRefresh, fetchEnseignants]);

  // Gestion des actions
  const handleAdd = () => {
    setSelectedEnseignant(null);
    setIsModalOpen(true);
  };

  const handleEdit = (enseignant) => {
    setSelectedEnseignant(enseignant);
    setIsModalOpen(true);
  };

  const handleDelete = (enseignant) => {
    setEnseignantToDelete(enseignant);
    setIsConfirmModalOpen(true);
  };

  const handleCreateAccount = (enseignant) => {
    setEnseignantForAccount(enseignant);
    setIsCreateAccountModalOpen(true);
  };

  const handleAccountCreated = () => {
    // Rafraîchir la liste des enseignants après création du compte
    fetchEnseignants();
  };

  const handleSubmit = async (formData) => {
    const result = selectedEnseignant 
      ? await updateEnseignant(selectedEnseignant.id, formData)
      : await createEnseignant(formData);

    if (result.success) {
      setIsModalOpen(false);
      setSelectedEnseignant(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (enseignantToDelete) {
      const result = await deleteEnseignant(enseignantToDelete.id);
      if (result.success) {
        setIsConfirmModalOpen(false);
        setEnseignantToDelete(null);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEnseignant(null);
  };

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
    setEnseignantToDelete(null);
  };

  const handleCloseCreateAccountModal = () => {
    setIsCreateAccountModalOpen(false);
    setEnseignantForAccount(null);
  };

  // Composant de pagination
  const PaginationComponent = () => {
    if (pagination.total_pages <= 1) return null;

    return (
      <div className="flex items-center justify-between bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => handlePageChange(pagination.current_page - 1)}
            disabled={!pagination.has_previous}
            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Précédent
          </button>
          <button
            onClick={() => handlePageChange(pagination.current_page + 1)}
            disabled={!pagination.has_next}
            className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Suivant
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Affichage de{' '}
              <span className="font-medium">
                {((pagination.current_page - 1) * pagination.page_size) + 1}
              </span>{' '}
              à{' '}
              <span className="font-medium">
                {Math.min(pagination.current_page * pagination.page_size, pagination.count)}
              </span>{' '}
              sur{' '}
              <span className="font-medium">{pagination.count}</span> résultats
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={!pagination.has_previous}
                className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              {/* Numéros de page */}
              {[...Array(pagination.total_pages)].map((_, index) => {
                const pageNum = index + 1;
                const isCurrentPage = pageNum === pagination.current_page;
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border ${
                      isCurrentPage
                        ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                        : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={!pagination.has_next}
                className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Barre de recherche */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Rechercher un enseignant..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-[#202654] focus:border-[#202654] w-full"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Tableau des enseignants */}
        <EnseignantTable
          enseignants={enseignants}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onCreateAccount={handleCreateAccount}
          searchTerm={searchTerm}
        />
        
        {/* Pagination */}
        <PaginationComponent />
      </div>

      {/* Modal du formulaire d'enseignant */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedEnseignant ? 'Modifier l\'enseignant' : 'Nouvel enseignant'}
        maxWidth="max-w-5xl"
      >
        <EnseignantForm
          initialData={selectedEnseignant}
          statusEnseignants={statusEnseignants}
          onSubmit={handleSubmit}
          submitting={submitting}
          onCancel={handleCloseModal}
        />
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmDelete}
        title="Supprimer l'enseignant"
        message={`Êtes-vous sûr de vouloir supprimer l'enseignant ${enseignantToDelete?.prenom} ${enseignantToDelete?.nom} ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
        isLoading={deleteLoading}
      />

      {/* Modal de création de compte enseignant */}
      <CreateEnseignantAccountModal
        isOpen={isCreateAccountModalOpen}
        onClose={handleCloseCreateAccountModal}
        enseignant={enseignantForAccount}
        onSuccess={handleAccountCreated}
      />
    </>
  );
};

export default EnseignantTab; 